// This script is responsible for all google-related API calls.
// Do not add wrapper-specific code here.

import { IS_DEV, IS_NODE, IS_TESTING } from "@/__main__/constants.mjs";
import featureFlags, { allowRead } from "@/__main__/feature-flags.mjs";
import router, { EVENT_CHANGE_ROUTE } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { EVENT_ERROR } from "@/app/ErrorBoundary.jsx";
import { ADS_COLUMN_CLASS } from "@/feature-ads/AdWrapper.jsx";
import adsMasterList from "@/feature-ads/constants/ads-master-list.json";
import adsMasterListDev from "@/feature-ads/constants/ads-master-list-dev.json";
import {
  AD_VIEW_THRESHOLD,
  SLOT_UNFILLED_CLASS,
} from "@/feature-ads/constants/constants.mjs";
import { EVENT_TRACK_AD_VIEW } from "@/feature-ads/constants/events.mjs";
import Slot from "@/feature-ads/constants/slot-symbols.mjs";
import { setTargeting } from "@/feature-ads/display-events.mjs";
import fetchRemoteAdConfig from "@/feature-ads/fetch-remote-ad-config.mjs";
import adsRefs from "@/feature-ads/refs.mjs";
import { isAppVisible } from "@/feature-ads/util.mjs";
import { checkPageVisibility } from "@/feature-ads/util/check-page-visibility.mjs";
// import { isWindowHidden } from "@/feature-ads/util/is-window-hidden.mjs";
import {
  promiseTimeout,
  promiseTimeoutOk,
} from "@/feature-ads/util/promise-timeout.mjs";
import { AD_FORMATS } from "@/feature-analytics/hooks-ads.mjs";
import clone from "@/util/clone.mjs";
import debounce from "@/util/debounce.mjs";
import { devDebug, devError } from "@/util/dev.mjs";
import { removeFromArray } from "@/util/helpers.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";
import { fetchCountryCode } from "@/util/use-country-code.mjs";

// eslint-disable-next-line no-restricted-properties
const g = globalThis;

const masterList = IS_DEV ? adsMasterListDev : adsMasterList;
export const elementSlotMap = new Map();
const slotIntersectionPromiseMap = new WeakMap();

const AD_REFRESH_DEFAULT = true;
const DEFAULT_REFRESH = 60 * 1000; // 1 minute

export const CONFIG = makeStrictKeysObject({
  SCRIPTS: adsRefs.SCRIPTS,
  AMAZON_PUB_ID: "8afe73dc-815b-482b-aa2f-721829d05f3d",

  REFRESH_MS: DEFAULT_REFRESH,
  REFRESH_MIN: 30 * 1000, // sane absolute minimum refresh
  REFRESH_MOD: 1.0, // conditionally throttle refreshes by this factor
  RECREATE_SLOT_AFTER: 2, // recreate slots after this many refreshes, see recreateSlots()

  SCRIPT_TIMEOUT: IS_NODE ? 0 : 8 * 1000,
  BID_TIMEOUT: 4 * 1000,
  MAX_RES: [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],

  REFRESH_FREQUENCY_DEBOUNCE_MS: 3 * 1000,
  ADS_REFRESH_COUNT_MAX: 300,
});

const vendorSetup = {
  GPT_SRC() {
    googleCmd(googleInit);
  },
};
Object.assign(adsRefs.vendorSetup, vendorSetup);

// we should probably consider using googletag.pubads().getSlots() instead
// of maintaining our own list.
export const googleSlots = [];
export const slotSizeMap = new WeakMap();

function googleCmd<R>(fn: () => R | Promise<R>) {
  if (IS_TESTING) return;
  (g.googletag ||= {}).cmd ||= [];
  return new Promise<R>((resolve, reject) => {
    g.googletag.cmd.push(async () => {
      try {
        resolve(await fn());
      } catch (e) {
        reject(e);
      }
    });
  });
}

adsRefs.googleCmd = googleCmd;

export function initScript(src) {
  if (adsRefs.initStates[src]) return adsRefs.initStates[src];
  const script = g.document.createElement("script");
  script.src = src;
  script.async = true;
  const promise = new Promise<void>((resolve, reject) => {
    script.addEventListener("load", () => {
      resolve();
      adsRefs.vendorSetup[src]?.();
    });
    script.addEventListener("error", (error) => {
      adsRefs.initStates[src] = undefined;
      reject(error);
    });
  });
  adsRefs.initStates[src] = promiseTimeout(promise, CONFIG.SCRIPT_TIMEOUT);
  g.document.body.appendChild(script);
  return promise;
}

const targetingHandler = (key, value, slot) => {
  googleCmd(() => {
    slot ||= g.googletag.pubads();
    if (value) return slot.setTargeting(key, String(value));
    // only clear if key exists, otherwise a warning is thrown
    const curArr = slot.getTargeting(key);
    if (curArr?.length > 0) slot.clearTargeting(key);
  });
};

adsRefs.targetingHandler = targetingHandler;

const RESPONSE_INFORMATION_KEYS = [
  "advertiserId",
  "campaignId",
  "isBackfill",
  "sourceAgnosticCreativeId",
  "sourceAgnosticLineItemId",
  "yieldGroupIds",
];

let googleInitialized = false;
function googleInit() {
  g.googletag.pubads().disableInitialLoad();
  g.googletag.pubads().enableSingleRequest();
  g.googletag.pubads().collapseEmptyDivs();
  g.googletag.pubads().enableLazyLoad({ fetchMarginPercent: -1 });
  g.googletag.pubads().setForceSafeFrame(true);

  g.googletag.enableServices();

  if (googleInitialized) return;
  googleInitialized = true;
  g.googletag.pubads().addEventListener("slotResponseReceived", (event) => {
    const { slot } = event;
    slot[Slot.refreshPromise]?.resolve(slot);
    const responseInformation = slot.getResponseInformation();

    slot[Slot.lastResponse] = {
      slot: slot.getSlotElementId(),
      adUnit: slot.getAdUnitPath(),
      response: slot.getResponseInformation(),
      targeting: Object.fromEntries(
        slot.getTargetingKeys().map((key) => [key, slot.getTargeting(key)[0]]),
      ),
    };

    if (!responseInformation) {
      for (const [element, s] of elementSlotMap.entries()) {
        if (s !== slot) continue;
        element.parentNode.classList.add(SLOT_UNFILLED_CLASS);
      }
    }
  });
  // "impressionViewable" is triggered when the ad view reaches the 'viewable' state, that is, at least 50% of the ad
  // are is visible in the viewport and has been visible for at least 1s.
  g.googletag.pubads().addEventListener("impressionViewable", (event) => {
    const { slot } = event;
    const responseInformation = slot.getResponseInformation();
    if (!responseInformation) return; // ignore unfilled slots

    // Do not modify original object to be safe
    const bidData = clone(slot.bidData);
    delete bidData?.wb?.adHtml; // this is huge and not needed

    let tudeServe;
    try {
      tudeServe = g.tude.isTudeserveTestEnabled();
    } catch (e) {
      devDebug("cant get tude serve from aditude", e);
    }

    const adView: ArbitraryMap<unknown> = {
      format: AD_FORMATS.DISPLAY,
      slotId: slot.getSlotElementId(),
      slotName: slot.getAdUnitPath(),
      size: slotSizeMap.get(slot),
      bidData,
      // tudeServe=true: aditude ads serve, otherwise google ads serve
      tudeServe,
    };
    adView.kvs = getResolvedTargeting(adView.slotId);
    for (const key of RESPONSE_INFORMATION_KEYS)
      adView[key] = responseInformation[key];
    eventBus.emit(EVENT_TRACK_AD_VIEW, adView);
  });
}

adsRefs.googleInit = googleInit;

let refreshTimer = null;

// get refresh time info
async function getRefreshTime() {
  const active = await isAppVisible();
  let ms = CONFIG.REFRESH_MS;
  if (!active) ms *= CONFIG.REFRESH_MOD;
  ms = Math.max(ms, CONFIG.REFRESH_MIN);
  return {
    ms, // used for setTimeout
    sec: Math.floor(ms / 1000), // used for reporting
    mod: !active, // used for reporting
  };
}

async function queueRefresh(slotId?) {
  const { ms, sec, mod } = await getRefreshTime();
  // setting these kvs early allows us to report these kvs for the current
  // refresh bid instead of the next refresh
  clearTimeout(refreshTimer);
  setTargeting("refresh_rate", sec);
  setTargeting("mod", mod ? "1" : "0");
  refreshTimer = setTimeout(() => {
    if (adsRefs.canRefresh()) {
      refreshSlots();
    }
    queueRefresh(slotId);
  }, ms);
}

export function registerSlot(slotId) {
  return googleCmd(() => {
    for (const id in masterList) {
      const {
        slot: slotName,
        sizes,
        safeFrame: slotSafeFrame,
        refresh: slotRefresh,
      } = masterList[id];
      if (slotId !== id) continue;

      // Destroy existing slot. If we don't call this we might make invalid calls on SSR error.
      // We cannot re-use the existing slot (even if assigned to a new element) as it becomes
      // flaky when we try to refresh it.
      // NOTE: use `filter` to catch possible duplicates which will lead to an
      // invalid state
      const existingSlots = googleSlots.filter(
        (s) => s.getSlotElementId() === id,
      );
      for (const existingSlot of existingSlots) {
        g.googletag.destroySlots([existingSlot]);
        removeFromArray(googleSlots, existingSlot);
      }

      const slot = g.googletag.defineSlot(slotName, sizes, id);
      if (typeof slotSafeFrame === "boolean")
        slot.setForceSafeFrame(slotSafeFrame);
      slot.addService(g.googletag.pubads());

      if (IS_DEV) slot.set("adsense_test_mode", "on");

      defineSlotSize(slot, sizes);

      slot[Slot.shouldRefresh] = slotRefresh ?? AD_REFRESH_DEFAULT;

      googleSlots.push(slot);
      const element = g.document.getElementById(id);

      if (!element) {
        devError(`[ads] element not found for id "${id}"`);
        return;
      }

      // This is a hidden requirement for IntersectionObserver to work
      // properly: if the area is zero, then it will not report anything
      // correctly.
      const [width, height] = sizes[0];
      const isOutOfPage = width === 1 && height === 1;
      if (isOutOfPage) {
        element.style.minWidth = "1px";
        element.style.minHeight = "1px";
      } else {
        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
      }

      slotIntersectionPromiseMap.set(slot, observeAd(element));
      elementSlotMap.set(element, slot);
    }
    queueRefresh(slotId);
  });
}

function observeAd(element) {
  if (!(element instanceof HTMLElement)) {
    // Something very bad happened...
    return Promise.reject(
      new Error(`Expected element to observeAd, got "${element}"`),
    );
  }
  return new Promise<void>((resolve) => {
    // pubads will set display: none when an ad is not filled
    // this will interfere with the intersection observer unless we
    // remove the style property
    const mutObs = new MutationObserver(() => {
      if (element.style.display === "none")
        element.style.removeProperty("display");
    });
    mutObs.observe(element, { attributeFilter: ["style"] });

    const obs = new IntersectionObserver(
      ([{ isIntersecting }]) => {
        resolve();
        const slot = elementSlotMap.get(element);
        if (!slot) {
          devDebug("slot not found", element);
          return;
        }
        slot[Slot.isViewable] = isIntersecting;
      },
      {
        threshold: AD_VIEW_THRESHOLD,
      },
    );
    obs.observe(element);
  });
}

export function destroySlots(slots) {
  return new Promise<void>((resolve) => {
    googleCmd(() => {
      g.googletag.destroySlots(slots);
      for (const slot of slots) {
        removeFromArray(googleSlots, slot);
        for (const [element, otherSlot] of elementSlotMap) {
          if (slot === otherSlot) elementSlotMap.delete(element);
        }
      }
      resolve();
    });
  });
}

async function checkViewableSlots() {
  await Promise.all(
    googleSlots.map((slot) => slotIntersectionPromiseMap.get(slot)),
  );
  if (
    !googleSlots.find((slot) => {
      return slot[Slot.isViewable];
    })
  ) {
    devError("No viewable slots!");
  }
}

const isRefreshRequestedSemaphores = {};
const ALL_CONSTANT = "ALL";
export function refreshSlots(slotIds?) {
  return Promise.all(
    (slotIds ?? [ALL_CONSTANT]).map(async (slotId) => {
      if (
        isRefreshRequestedSemaphores[slotId] ||
        (slotIds && isRefreshRequestedSemaphores[ALL_CONSTANT])
      )
        return;
      isRefreshRequestedSemaphores[slotId] = true;

      await doRefresh(slotId === ALL_CONSTANT ? undefined : slotId);

      isRefreshRequestedSemaphores[slotId] = false;
    }),
  );
}

function defineSlotSize(slot, sizes) {
  const s = g.googletag.sizeMapping();
  // Trolling because viewport size doesn't matter.
  const viewport = CONFIG.MAX_RES;
  for (const size of sizes) {
    s.addSize(viewport, size);
  }
  const sizeMapping = s.build();
  slot.defineSizeMapping(sizeMapping);

  // Guaranteeing that slot sizes are accessible.
  // There is also the undocumented slot.getSizes() method.
  slotSizeMap.set(slot, sizes);
}

async function doRefresh(slotId) {
  let secondaryTag = "display_ad_init";
  try {
    // Don't refresh at all in this state.
    // if (await isWindowHidden()) return;

    // Refresh only when the page is visible!
    secondaryTag = "display_ad_page_visibility";
    await checkPageVisibility();

    // pull in the latest config, non-blocking
    fetchRemoteAdConfig();

    secondaryTag = "display_ad_viewable_slots";
    await checkViewableSlots();

    const targetedSlots = googleSlots.filter((slot) => {
      if (!slot[Slot.isViewable]) return false;
      if (!slotId) return true;
      return slotId === slot.getSlotElementId();
    });
    if (!targetedSlots.length) return;

    // Reset unfilled status.
    for (const slot of targetedSlots) {
      for (const [element, s] of elementSlotMap.entries()) {
        if (s !== slot) continue;
        element.parentNode.classList.remove(SLOT_UNFILLED_CLASS);
      }
    }

    secondaryTag = "display_ad_set_targeting";
    await googleCmd(() => {
      const now = Date.now();
      // add slot-specific kvs
      for (const slot of targetedSlots) {
        // kv: refresh_time (seconds since last refresh)
        // this differs from refresh_rate, as rate is the nominal config,
        // and time is the actual time elapsed
        const lastRefreshTime = slot[Slot.refreshedAt] || 0;
        const deltaMs = lastRefreshTime && now - lastRefreshTime;
        setTargeting("refresh_time", Math.round(deltaMs / 1000), slot); // 0 is first refresh
        slot[Slot.refreshedAt] = now;
        // kv: refresh_count (incremented on each refresh)
        slot[Slot.refreshedCount] ||= 0;
        setTargeting("refresh_count", slot[Slot.refreshedCount]++, slot);
      }

      // kv: rr_count (number of viewable ads in the rr column)
      setTargeting(
        "rr_count",
        g.document.querySelectorAll(`.${ADS_COLUMN_CLASS} .viewable`).length,
      );
    });

    secondaryTag = "display_ad_internal_refresh";
    await adsRefs.internalRefresh(targetedSlots);
  } catch (e) {
    await promiseTimeoutOk(
      googleCmd(() => {
        e["targeting"] = getTargetingMaps();
      }),
      CONFIG.BID_TIMEOUT,
    );

    // we mirror non-error props so we can see them in the console
    devError("Failed to refresh ad slot", e, { targeting: e?.["targeting"] });
    // we don't care about mapped output, this most likely came from vendor code
    if (!IS_DEV)
      eventBus.emit(EVENT_ERROR, {
        error: e,
        tags: ["display_ad", secondaryTag],
      });
  }
}

function getResolvedTargeting(slotId) {
  const { "#page": pageKvs, [slotId]: slotKvs } = getTargetingMaps(slotId);
  const kvs = {
    ...pageKvs,
    ...slotKvs,
  };
  // remove vendor kvs
  for (const key in kvs) {
    if (key.startsWith("hb_")) delete kvs[key]; // prebid (header bidding)
    if (key.startsWith("pwt")) delete kvs[key]; // pubmatic
    if (key.startsWith("amzn")) delete kvs[key]; // amazon
  }
  return kvs;
}

function getTargetingMaps(slotId?) {
  const targeting: Record<string, Record<string, string>> = {};
  targeting["#page"] = slotGetTargetingMap(g.googletag.pubads());
  const slots = googleSlots.filter((slot) => {
    if (!slot[Slot.isViewable]) return false;
    if (!slotId) return true;
    return slotId === slot.getSlotElementId();
  });
  for (const slot of slots)
    targeting[slot.getSlotElementId()] = slotGetTargetingMap(slot);
  return targeting;
}

// differs from setTargeting in that this takes a record of kvs
function _setKVs(slot, kvs) {
  for (const key in kvs) setTargeting(key, kvs[key], slot);
}

function slotGetTargetingMap(slot) {
  const targeting: Record<string, string> = {};
  const keys = slot.getTargetingKeys();
  for (const key of keys) {
    const value = slot.getTargeting(key);
    targeting[key] = value.join(",");
  }
  return targeting;
}

export function getPageTargetingMap() {
  if (!g.googletag) return;
  return slotGetTargetingMap(g.googletag.pubads());
}

const debouncedRefreshSlots = debounce(
  () => refreshSlots(),
  CONFIG.REFRESH_FREQUENCY_DEBOUNCE_MS,
  { leading: true, trailing: false },
);

export const subscribeToRouteChangeEvents = () => {
  let isAdsStillEnabledAfterFetchingCountryCode = false;

  async function setRouteListener() {
    const countryCode = await fetchCountryCode({ skipSafetyCheck: true });

    allowRead();
    isAdsStillEnabledAfterFetchingCountryCode = featureFlags.ads;
    if (countryCode === "IL" && isAdsStillEnabledAfterFetchingCountryCode)
      router.events.on(EVENT_CHANGE_ROUTE, debouncedRefreshSlots);
  }
  setRouteListener();

  return () => {
    if (isAdsStillEnabledAfterFetchingCountryCode)
      router.events.off(EVENT_CHANGE_ROUTE, debouncedRefreshSlots);
  };
};
